import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, X, Clock, ArrowLeft, Link2, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type RegistrationRequest = {
  id: string;
  email: string;
  naam: string;
  bedrijf: string;
  telefoon: string | null;
  wachtwoord_hash: string | null;
  status: "pending" | "approved" | "rejected";
  requested_role: "medewerker" | "planner" | "management";
  matched_medewerker_id: string | null;
  created_at: string;
  reviewed_at: string | null;
  rejection_reason: string | null;
};

type Medewerker = {
  id: string;
  naam: string;
};

const AdminRegistraties = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedRequest, setSelectedRequest] = React.useState<RegistrationRequest | null>(null);
  const [selectedRole, setSelectedRole] = React.useState<string>("");
  const [selectedMedewerkerId, setSelectedMedewerkerId] = React.useState<string>("");
  const [rejectionReason, setRejectionReason] = React.useState("");
  const [showApproveDialog, setShowApproveDialog] = React.useState(false);
  const [showRejectDialog, setShowRejectDialog] = React.useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState<RegistrationRequest | null>(null);

  const { data: requests, isLoading } = useQuery({
    queryKey: ["registration-requests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("registration_requests")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as RegistrationRequest[];
    },
  });

  const { data: medewerkers } = useQuery({
    queryKey: ["medewerkers-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("medewerkers")
        .select("id, naam")
        .order("naam");
      if (error) throw error;
      return data as Medewerker[];
    },
  });

  const getMedewerkerNaam = (id: string | null) => {
    if (!id || !medewerkers) return null;
    return medewerkers.find(m => m.id === id)?.naam || null;
  };

  const approveMutation = useMutation({
    mutationFn: async ({ id, role, medewerkerId }: { id: string; role: string; medewerkerId: string | null }) => {
      const { data, error } = await supabase.functions.invoke("approve-registration", {
        body: { requestId: id, role, medewerkerId },
      });
      if (error) throw new Error(error.message || "Goedkeuring mislukt");
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      toast.success("Registratie goedgekeurd", {
        description: "Gebruiker heeft een e-mail ontvangen met instructies.",
      });
      queryClient.invalidateQueries({ queryKey: ["registration-requests"] });
      setShowApproveDialog(false);
      setSelectedRequest(null);
    },
    onError: (error: any) => {
      toast.error("Goedkeuring mislukt", { description: error.message });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const request = requests?.find(r => r.id === id);
      if (!request) throw new Error("Request not found");

      const { error } = await supabase
        .from("registration_requests")
        .update({
          status: "rejected",
          rejection_reason: reason,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) throw error;

      try {
        await supabase.functions.invoke("send-registration-email", {
          body: {
            type: "rejection",
            email: request.email,
            name: request.naam,
            rejectionReason: reason,
          },
        });
      } catch (emailError) {
        console.error("Failed to send rejection email:", emailError);
      }
    },
    onSuccess: () => {
      toast.success("Registratie afgewezen");
      queryClient.invalidateQueries({ queryKey: ["registration-requests"] });
      setShowRejectDialog(false);
      setSelectedRequest(null);
      setRejectionReason("");
    },
    onError: (error: any) => {
      toast.error("Afwijzing mislukt", { description: error.message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("registration_requests")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Registratie verwijderd", {
        description: "De gebruiker kan nu opnieuw registreren.",
      });
      queryClient.invalidateQueries({ queryKey: ["registration-requests"] });
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    },
    onError: (error: any) => {
      toast.error("Verwijderen mislukt", { description: error.message });
    },
  });

  const handleDelete = (request: RegistrationRequest) => {
    setDeleteTarget(request);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id);
    }
  };

  const handleApprove = (request: RegistrationRequest) => {
    setSelectedRequest(request);
    setSelectedRole(request.requested_role);
    setSelectedMedewerkerId(request.matched_medewerker_id || "none");
    setShowApproveDialog(true);
  };

  const handleReject = (request: RegistrationRequest) => {
    setSelectedRequest(request);
    setShowRejectDialog(true);
  };

  const confirmApprove = () => {
    if (selectedRequest) {
      approveMutation.mutate({
        id: selectedRequest.id,
        role: selectedRole,
        medewerkerId: selectedMedewerkerId === "none" ? null : selectedMedewerkerId,
      });
    }
  };

  const confirmReject = () => {
    if (selectedRequest && rejectionReason.trim()) {
      rejectMutation.mutate({ id: selectedRequest.id, reason: rejectionReason });
    }
  };

  const pendingRequests = requests?.filter(r => r.status === "pending") || [];
  const reviewedRequests = requests?.filter(r => r.status !== "pending") || [];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="warning" className="gap-1 px-3 py-1 text-sm"><Clock className="h-3.5 w-3.5" /> In afwachting</Badge>;
      case "approved":
        return <Badge className="gap-1 px-3 py-1 text-sm bg-green-600 text-white border-transparent"><Check className="h-3.5 w-3.5" /> Goedgekeurd</Badge>;
      case "rejected":
        return <Badge variant="destructive" className="gap-1 px-3 py-1 text-sm"><X className="h-3.5 w-3.5" /> Afgewezen</Badge>;
      default:
        return null;
    }
  };

  const getCardBorderClass = (status: string) => {
    switch (status) {
      case "pending": return "border-l-4 border-l-orange-400";
      case "approved": return "border-l-4 border-l-green-500";
      case "rejected": return "border-l-4 border-l-red-500";
      default: return "";
    }
  };

  const getRoleBadge = (role: string) => {
    const variants: Record<string, string> = {
      medewerker: "secondary",
      planner: "default",
      management: "destructive",
    };
    return <Badge variant={variants[role] as any}>{role}</Badge>;
  };

  const RequestCard = ({ request, showActions = false }: { request: RegistrationRequest; showActions?: boolean }) => (
    <Card key={request.id} className={getCardBorderClass(request.status)}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle>{request.naam}</CardTitle>
            <CardDescription>{request.email}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(request.status)}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => handleDelete(request)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Bedrijf</p>
            <p className="font-medium">{request.bedrijf || "-"}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Telefoon</p>
            <p className="font-medium">{request.telefoon || "-"}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Gekoppelde medewerker</p>
            <div className="mt-1">
              {request.matched_medewerker_id ? (
                <Badge variant="outline" className="gap-1">
                  <Link2 className="h-3 w-3" />
                  {getMedewerkerNaam(request.matched_medewerker_id) || "Onbekend"}
                </Badge>
              ) : (
                <span className="text-muted-foreground text-xs">Geen match gevonden</span>
              )}
            </div>
          </div>
          <div>
            <p className="text-muted-foreground">Aangevraagd op</p>
            <p className="font-medium">
              {new Date(request.created_at).toLocaleDateString("nl-NL")}
            </p>
          </div>
          {request.reviewed_at && (
            <div>
              <p className="text-muted-foreground">Beoordeeld op</p>
              <p className="font-medium">
                {new Date(request.reviewed_at).toLocaleDateString("nl-NL")}
              </p>
            </div>
          )}
        </div>

        {showActions && (
          <div className="flex gap-2">
            <Button onClick={() => handleApprove(request)} className="flex-1" variant="default">
              <Check className="h-4 w-4 mr-2" /> Goedkeuren
            </Button>
            <Button onClick={() => handleReject(request)} className="flex-1" variant="destructive">
              <X className="h-4 w-4 mr-2" /> Afwijzen
            </Button>
          </div>
        )}

        {!showActions && request.rejection_reason && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm font-medium text-red-800">Reden afwijzing</p>
            <p className="text-sm text-red-700 mt-1">{request.rejection_reason}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <Card><CardContent className="p-6"><p>Laden...</p></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Terug naar dashboard
        </Button>
        <h1 className="text-3xl font-bold">Registratie Aanvragen</h1>
        <p className="text-muted-foreground">Beheer nieuwe gebruikers registraties</p>
      </div>

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending" className="gap-2">
            In afwachting
            {pendingRequests.length > 0 && <Badge variant="secondary">{pendingRequests.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="reviewed">Afgehandeld</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          {pendingRequests.length === 0 ? (
            <Card><CardContent className="p-6 text-center text-muted-foreground">Geen openstaande aanvragen</CardContent></Card>
          ) : (
            pendingRequests.map((request) => <RequestCard key={request.id} request={request} showActions />)
          )}
        </TabsContent>

        <TabsContent value="reviewed" className="space-y-4">
          {reviewedRequests.length === 0 ? (
            <Card><CardContent className="p-6 text-center text-muted-foreground">Geen afgehandelde aanvragen</CardContent></Card>
          ) : (
            reviewedRequests.map((request) => <RequestCard key={request.id} request={request} />)
          )}
        </TabsContent>
      </Tabs>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registratie goedkeuren</DialogTitle>
            <DialogDescription>
              Keur de registratie van {selectedRequest?.naam} goed en wijs een rol toe.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Rol toewijzen</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="medewerker">Medewerker</SelectItem>
                  <SelectItem value="planner">Planner</SelectItem>
                  <SelectItem value="management">Management</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Koppelen aan medewerker</Label>
              <Select value={selectedMedewerkerId} onValueChange={setSelectedMedewerkerId}>
                <SelectTrigger><SelectValue placeholder="Selecteer medewerker..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Geen koppeling</SelectItem>
                  {medewerkers?.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.naam}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedRequest?.matched_medewerker_id && selectedMedewerkerId === selectedRequest.matched_medewerker_id && (
                <p className="text-xs text-green-600">✓ Automatisch gekoppeld op basis van naam</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>Annuleren</Button>
            <Button onClick={confirmApprove} disabled={approveMutation.isPending}>
              {approveMutation.isPending ? "Goedkeuren..." : "Goedkeuren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Registratie verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je de registratie van <strong>{deleteTarget?.naam}</strong> wilt verwijderen? De gebruiker kan hierna opnieuw registreren.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Verwijderen..." : "Verwijderen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registratie afwijzen</DialogTitle>
            <DialogDescription>
              Geef een reden op waarom de registratie van {selectedRequest?.naam} wordt afgewezen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Reden *</Label>
              <Textarea
                placeholder="Bijvoorbeeld: Geen geldige functie binnen het bedrijf"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Annuleren</Button>
            <Button
              variant="destructive"
              onClick={confirmReject}
              disabled={!rejectionReason.trim() || rejectMutation.isPending}
            >
              {rejectMutation.isPending ? "Afwijzen..." : "Afwijzen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRegistraties;
